import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { ArenaType } from "@/components/live-arenas/ArenaTypeBadge";

export interface LiveArena {
  id: string;
  name: string;
  topic: string;
  description: string | null;
  city_id: string | null;
  country_code: string | null;
  max_participants: number;
  language: string;
  is_video_enabled: boolean;
  is_voice_only: boolean;
  participants_count: number;
  is_joined: boolean;
  created_by: string | null;
  arena_type?: string;
  is_trending?: boolean;
}

export interface ArenaParticipant {
  id: string;
  user_id: string;
  is_speaker: boolean;
  is_muted: boolean;
  is_video_on: boolean;
  joined_at: string;
  hand_raised?: boolean;
  hand_raised_at?: string | null;
  profile?: {
    id: string;
    name: string;
    photo_url: string | null;
  };
}

export interface ArenaFiltersConfig {
  language?: string | null;
  arenaType?: ArenaType | null;
  minParticipants?: number;
  maxParticipants?: number;
}

export interface CreateArenaParams {
  name: string;
  topic: string;
  description: string;
  arena_type: ArenaType;
  language: string;
  max_participants: number;
  is_video_enabled: boolean;
  is_voice_only: boolean;
}

export const useLiveArenas = (countryCode?: string, cityId?: string, filters?: ArenaFiltersConfig) => {
  const { user } = useAuthContext();
  const [arenas, setArenas] = useState<LiveArena[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentArena, setCurrentArena] = useState<LiveArena | null>(null);
  const [participants, setParticipants] = useState<ArenaParticipant[]>([]);

  // Fetch arenas with filters
  const fetchArenas = useCallback(async () => {
    try {
      let query = supabase
        .from("live_arenas")
        .select(`
          *,
          arena_participants(count)
        `)
        .eq("is_active", true);

      if (countryCode) {
        query = query.eq("country_code", countryCode);
      }
      if (cityId) {
        query = query.eq("city_id", cityId);
      }
      if (filters?.language) {
        query = query.eq("language", filters.language);
      }
      if (filters?.arenaType) {
        query = query.eq("arena_type", filters.arenaType);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get user's participation
      let userParticipation: string[] = [];
      if (user) {
        const { data: participation } = await supabase
          .from("arena_participants")
          .select("arena_id")
          .eq("user_id", user.id)
          .is("left_at", null);
        
        userParticipation = participation?.map(p => p.arena_id) || [];
      }

      let arenasWithCounts = data?.map(arena => ({
        ...arena,
        participants_count: arena.arena_participants?.[0]?.count || 0,
        is_joined: userParticipation.includes(arena.id),
      })) || [];

      // Apply participant count filter client-side
      if (filters?.minParticipants !== undefined) {
        arenasWithCounts = arenasWithCounts.filter(
          a => a.participants_count >= (filters.minParticipants || 0)
        );
      }
      if (filters?.maxParticipants !== undefined && filters.maxParticipants !== Infinity) {
        arenasWithCounts = arenasWithCounts.filter(
          a => a.participants_count <= (filters.maxParticipants || Infinity)
        );
      }

      setArenas(arenasWithCounts);
    } catch (error) {
      console.error("Error fetching arenas:", error);
    } finally {
      setLoading(false);
    }
  }, [countryCode, cityId, user, filters?.language, filters?.arenaType, filters?.minParticipants, filters?.maxParticipants]);

  // Fetch participants for an arena
  const fetchParticipants = useCallback(async (arenaId: string) => {
    try {
      const { data, error } = await supabase
        .from("arena_participants")
        .select(`
          *,
          profiles:user_id (
            id,
            name,
            photo_url
          )
        `)
        .eq("arena_id", arenaId)
        .is("left_at", null);

      if (error) throw error;

      const participantsWithProfiles = data?.map(p => ({
        ...p,
        profile: p.profiles as unknown as ArenaParticipant["profile"],
      })) || [];

      setParticipants(participantsWithProfiles);
    } catch (error) {
      console.error("Error fetching participants:", error);
    }
  }, []);

  // Create a new arena
  const createArena = useCallback(async (params: CreateArenaParams): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase
        .from("live_arenas")
        .insert({
          name: params.name,
          topic: params.topic,
          description: params.description || null,
          arena_type: params.arena_type,
          language: params.language,
          max_participants: params.max_participants,
          is_video_enabled: params.is_video_enabled,
          is_voice_only: params.is_voice_only,
          created_by: user.id,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-join the arena as host/speaker
      await supabase
        .from("arena_participants")
        .insert({
          arena_id: data.id,
          user_id: user.id,
          is_speaker: true,
          is_muted: false,
          is_video_on: params.is_video_enabled,
        });

      // Refresh arenas list
      await fetchArenas();

      // Set as current arena
      setCurrentArena({
        ...data,
        participants_count: 1,
        is_joined: true,
      });

      await fetchParticipants(data.id);

      return true;
    } catch (error) {
      console.error("Error creating arena:", error);
      return false;
    }
  }, [user, fetchArenas, fetchParticipants]);

  // Join arena
  const joinArena = useCallback(async (arenaId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("arena_participants")
        .upsert({
          arena_id: arenaId,
          user_id: user.id,
          is_speaker: false,
          is_muted: true,
          is_video_on: false,
        }, {
          onConflict: "arena_id,user_id",
        });

      if (error) throw error;

      // Update local state
      setArenas(prev => prev.map(a => 
        a.id === arenaId 
          ? { ...a, is_joined: true, participants_count: a.participants_count + 1 }
          : a
      ));

      const arena = arenas.find(a => a.id === arenaId);
      if (arena) setCurrentArena({ ...arena, is_joined: true });

      await fetchParticipants(arenaId);
      return true;
    } catch (error) {
      console.error("Error joining arena:", error);
      return false;
    }
  }, [user, arenas, fetchParticipants]);

  // Leave arena
  const leaveArena = useCallback(async (arenaId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("arena_participants")
        .update({ left_at: new Date().toISOString() })
        .eq("arena_id", arenaId)
        .eq("user_id", user.id);

      if (error) throw error;

      setArenas(prev => prev.map(a => 
        a.id === arenaId 
          ? { ...a, is_joined: false, participants_count: Math.max(0, a.participants_count - 1) }
          : a
      ));

      setCurrentArena(null);
      setParticipants([]);
      return true;
    } catch (error) {
      console.error("Error leaving arena:", error);
      return false;
    }
  }, [user]);

  // Toggle mute
  const toggleMute = useCallback(async (arenaId: string) => {
    if (!user) return;

    const participant = participants.find(p => p.user_id === user.id);
    if (!participant) return;

    const { error } = await supabase
      .from("arena_participants")
      .update({ is_muted: !participant.is_muted })
      .eq("arena_id", arenaId)
      .eq("user_id", user.id);

    if (!error) {
      setParticipants(prev => prev.map(p => 
        p.user_id === user.id ? { ...p, is_muted: !p.is_muted } : p
      ));
    }
  }, [user, participants]);

  // Toggle video
  const toggleVideo = useCallback(async (arenaId: string) => {
    if (!user) return;

    const participant = participants.find(p => p.user_id === user.id);
    if (!participant) return;

    const { error } = await supabase
      .from("arena_participants")
      .update({ is_video_on: !participant.is_video_on })
      .eq("arena_id", arenaId)
      .eq("user_id", user.id);

    if (!error) {
      setParticipants(prev => prev.map(p => 
        p.user_id === user.id ? { ...p, is_video_on: !p.is_video_on } : p
      ));
    }
  }, [user, participants]);

  // End arena (host only) - triggers recording capture
  const endArena = useCallback(async (arenaId: string) => {
    if (!user) return false;

    try {
      // Verify user is the host
      const arena = arenas.find(a => a.id === arenaId);
      if (!arena || arena.created_by !== user.id) {
        console.error("Only the host can end an arena");
        return false;
      }

      // Set arena as inactive - this triggers the recording capture
      const { error } = await supabase
        .from("live_arenas")
        .update({ 
          is_active: false,
          ended_at: new Date().toISOString()
        })
        .eq("id", arenaId);

      if (error) throw error;

      // Update local state
      setArenas(prev => prev.filter(a => a.id !== arenaId));
      setCurrentArena(null);
      setParticipants([]);

      console.log("Arena ended successfully, recording capture triggered");
      return true;
    } catch (error) {
      console.error("Error ending arena:", error);
      return false;
    }
  }, [user, arenas]);

  // Subscribe to realtime updates
  useEffect(() => {
    fetchArenas();

    const channel = supabase
      .channel("arena_updates")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "arena_participants" },
        () => {
          fetchArenas();
          if (currentArena) {
            fetchParticipants(currentArena.id);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchArenas, fetchParticipants, currentArena]);

  return {
    arenas,
    loading,
    currentArena,
    participants,
    joinArena,
    leaveArena,
    toggleMute,
    toggleVideo,
    fetchParticipants,
    setCurrentArena,
    endArena,
    createArena,
  };
};
